import React, { useState, useEffect } from 'react';
import api, { toArr } from '../api';

const C = {
  primary: '#6366f1', secondary: '#10b981', accent: '#f59e0b',
  card: 'rgba(255,255,255,0.05)', cardBorder: 'rgba(99,102,241,0.15)',
  text: '#fff', textMuted: 'rgba(255,255,255,0.5)',
};

const sampleQuestions = [
  { id: '1', category: 'GenAI', question: 'What is Retrieval Augmented Generation (RAG)?',
    options: [{ text: 'A technique combining retrieval with generation' }, { text: 'A neural network architecture' }, { text: 'A data preprocessing method' }, { text: 'A model fine-tuning approach' }],
    answer: 'A technique combining retrieval with generation',
    explanation: 'RAG combines information retrieval with text generation to produce more accurate, grounded responses.' },
  { id: '2', category: 'GenAI', question: 'Which of the following best describes a "Transformer" in AI?',
    options: [{ text: 'A recurrent neural network variant' }, { text: 'An attention-based sequence model' }, { text: 'A convolutional neural network' }, { text: 'A decision tree ensemble' }],
    answer: 'An attention-based sequence model',
    explanation: 'Transformers use self-attention mechanisms to process sequences, enabling parallel training and long-range dependencies.' },
  { id: '3', category: 'GenAI', question: 'What does "temperature" control in LLM generation?',
    options: [{ text: 'Model training speed' }, { text: 'GPU thermal management' }, { text: 'Randomness of token sampling' }, { text: 'Context window size' }],
    answer: 'Randomness of token sampling',
    explanation: 'Temperature scales the probability distribution before sampling; higher values = more random, lower = more deterministic.' },
];

// ── Quiz View ─────────────────────────────────────────────────────────────────

const QuizView = ({ questions, category, onFinish, onSaveResult, userId }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(questions.length * 60);
  const [resultSaved, setResultSaved] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTimeLeft(v => { if (v <= 1) { clearInterval(t); return 0; } return v - 1; }), 1000);
    return () => clearInterval(t);
  }, []);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const q = questions[currentQ];
  const isSubmitted = submitted[q?.id];
  const isCorrect = isSubmitted && answers[q?.id] === q?.answer;

  const allAnswered = questions.every(qx => submitted[qx.id]);
  const score = questions.filter(qx => answers[qx.id] === qx.answer).length;
  const percentage = Math.round((score / questions.length) * 100);
  const passed = percentage >= 60;

  // Save result when exam ends
  useEffect(() => {
    if ((allAnswered || timeLeft === 0) && !resultSaved) {
      setResultSaved(true);
      if (userId && userId !== 'demo-user-id') {
        api.post('/results', {
          userId, category, score, totalQuestions: questions.length,
          percentage, passed, completedAt: new Date().toISOString(),
        }).catch(() => {}); // non-blocking
      }
      onSaveResult?.({ category, score, percentage, passed });
    }
  }, [allAnswered, timeLeft, resultSaved]);  // eslint-disable-line

  if (allAnswered || timeLeft === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 72, marginBottom: 16 }}>{percentage >= 60 ? '🏆' : '📖'}</div>
        <div style={{ fontSize: '2.5rem', fontWeight: 900, color: C.text, marginBottom: 8 }}>
          {percentage}%
        </div>
        <div style={{ fontSize: '1rem', color: C.textMuted, marginBottom: 12 }}>
          {score} of {questions.length} correct
        </div>
        <div style={{ display: 'inline-block', padding: '6px 20px', borderRadius: 20, fontWeight: 700, fontSize: '0.9rem', marginBottom: 24,
          background: passed ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${passed ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
          color: passed ? C.secondary : '#ef4444' }}>
          {passed ? '✅ Passed!' : '❌ Not Passed — Try Again'}
        </div>

        {passed && (
          <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)',
            borderRadius: 16, padding: '20px 28px', maxWidth: 400, margin: '0 auto 28px', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, color: '#fff', marginBottom: 8 }}>🎓 Certificate Earned!</div>
            <div style={{ fontSize: '0.875rem', color: C.textMuted }}>
              {percentage >= 80
                ? 'Excellent performance! Your certificate has been recorded.'
                : 'You passed! Your certificate has been recorded. Aim for 80%+ to unlock distinction.'}
            </div>
          </div>
        )}

        <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.4)', marginBottom: 32 }}>
          {percentage >= 80 ? 'Excellent work!' : percentage >= 60 ? 'Good effort — keep learning!' : 'Review the material and try again.'}
        </div>

        {/* Score breakdown */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, maxWidth: 380, margin: '0 auto 32px' }}>
          {[
            { label: 'Correct', value: score, color: C.secondary },
            { label: 'Wrong', value: questions.length - score, color: '#ef4444' },
            { label: 'Score', value: `${percentage}%`, color: C.primary },
          ].map((s, i) => (
            <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.72rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
            </div>
          ))}
        </div>

        <button onClick={onFinish} style={{
          background: 'linear-gradient(135deg, #6366f1, #10b981)',
          border: 'none', borderRadius: 12, padding: '12px 32px',
          color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '1rem',
          boxShadow: '0 8px 24px rgba(99,102,241,0.4)',
        }}>Back to Tests</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <span style={{ fontSize: '0.75rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>
            Question {currentQ + 1} of {questions.length}
          </span>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 100, marginTop: 8, width: 220 }}>
            <div style={{ height: '100%', width: `${((currentQ + 1) / questions.length) * 100}%`,
              background: 'linear-gradient(90deg, #6366f1, #10b981)', borderRadius: 100, transition: 'width 0.3s' }} />
          </div>
        </div>
        <div style={{ background: timeLeft < 60 ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.15)',
          border: `1px solid ${timeLeft < 60 ? 'rgba(239,68,68,0.3)' : 'rgba(99,102,241,0.2)'}`,
          borderRadius: 10, padding: '8px 16px',
          color: timeLeft < 60 ? '#ef4444' : C.text,
          fontWeight: 700, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}>
          ⏱ {formatTime(timeLeft)}
        </div>
      </div>

      {/* Question */}
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 16 }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: C.primary,
          background: 'rgba(99,102,241,0.1)', display: 'inline-block',
          padding: '3px 10px', borderRadius: 20, marginBottom: 16, letterSpacing: 0.5 }}>
          {q.category}
        </div>
        <div style={{ fontSize: '1.05rem', fontWeight: 600, color: C.text, lineHeight: 1.6 }}>
          {q.question}
        </div>
      </div>

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
        {q.options.map((opt, i) => {
          const val = opt.text || opt;
          const isSelected = answers[q.id] === val;
          const correct = isSubmitted && val === q.answer;
          const wrong = isSubmitted && isSelected && val !== q.answer;
          return (
            <label key={i} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              background: correct ? 'rgba(16,185,129,0.12)' : wrong ? 'rgba(239,68,68,0.12)' : isSelected ? 'rgba(99,102,241,0.15)' : C.card,
              border: `1px solid ${correct ? '#10b981' : wrong ? '#ef4444' : isSelected ? C.primary : C.cardBorder}`,
              borderRadius: 12, padding: '14px 18px',
              cursor: isSubmitted ? 'default' : 'pointer',
              transition: 'all 0.2s',
            }}
              onMouseEnter={e => { if (!isSubmitted) e.currentTarget.style.borderColor = C.primary; }}
              onMouseLeave={e => { if (!isSubmitted && !isSelected) e.currentTarget.style.borderColor = C.cardBorder; }}
            >
              <input type="radio" name={q.id} value={val} checked={isSelected}
                onChange={() => !isSubmitted && setAnswers(p => ({ ...p, [q.id]: val }))}
                disabled={isSubmitted} style={{ display: 'none' }} />
              <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${correct ? '#10b981' : wrong ? '#ef4444' : isSelected ? C.primary : 'rgba(255,255,255,0.2)'}`,
                background: correct ? '#10b981' : wrong ? '#ef4444' : isSelected ? C.primary : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff' }}>
                {correct ? '✓' : wrong ? '✕' : ''}
              </div>
              <span style={{ color: correct ? '#10b981' : wrong ? '#ef4444' : C.text,
                fontSize: '0.9rem', fontWeight: isSelected ? 600 : 400 }}>
                {val}
              </span>
            </label>
          );
        })}
      </div>

      {/* Explanation */}
      {isSubmitted && q.explanation && (
        <div style={{ background: isCorrect ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${isCorrect ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.2)'}`,
          borderRadius: 12, padding: 16, marginBottom: 20, fontSize: '0.875rem', color: C.textMuted }}>
          <strong style={{ color: isCorrect ? '#10b981' : '#ef4444' }}>
            {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}
          </strong>
          {q.explanation}
        </div>
      )}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <button onClick={() => setCurrentQ(v => Math.max(0, v - 1))} disabled={currentQ === 0} style={{
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 10, padding: '10px 20px',
          color: currentQ === 0 ? 'rgba(255,255,255,0.2)' : C.text,
          cursor: currentQ === 0 ? 'not-allowed' : 'pointer', fontWeight: 600,
        }}>← Previous</button>

        <div style={{ display: 'flex', gap: 10 }}>
          {!isSubmitted && (
            <button onClick={() => setSubmitted(p => ({ ...p, [q.id]: true }))}
              disabled={!answers[q.id]} style={{
                background: answers[q.id] ? 'linear-gradient(135deg, #6366f1, #10b981)' : 'rgba(255,255,255,0.05)',
                border: 'none', borderRadius: 10, padding: '10px 24px',
                color: answers[q.id] ? '#fff' : 'rgba(255,255,255,0.3)',
                cursor: answers[q.id] ? 'pointer' : 'not-allowed', fontWeight: 600,
              }}>Submit Answer</button>
          )}
          <button onClick={() => setCurrentQ(v => Math.min(questions.length - 1, v + 1))}
            disabled={currentQ === questions.length - 1} style={{
              background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 10, padding: '10px 20px', color: C.text,
              cursor: currentQ < questions.length - 1 ? 'pointer' : 'not-allowed', fontWeight: 600,
            }}>Next →</button>
        </div>
      </div>
    </div>
  );
};

// ── Test History ──────────────────────────────────────────────────────────────

const TestHistory = ({ history }) => {
  if (!history.length) return null;
  const catColors = { GenAI: C.primary, Cloud: C.secondary, ML: C.accent, DevOps: '#ec4899' };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 24, marginTop: 28 }}>
      <div style={{ fontWeight: 700, color: '#fff', marginBottom: 16, fontSize: '0.95rem' }}>Recent Test Results</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {history.slice(-5).reverse().map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14,
            background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: r.passed ? C.secondary : '#ef4444' }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '0.65rem', fontWeight: 700,
                color: catColors[r.category] || C.primary,
                background: `${catColors[r.category] || C.primary}18`,
                padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {r.category}
              </span>
            </div>
            <div style={{ fontSize: '0.875rem', fontWeight: 700,
              color: r.passed ? C.secondary : '#ef4444' }}>
              {r.percentage}%
            </div>
            <div style={{ fontSize: '0.72rem', color: C.textMuted }}>
              {r.passed ? '✅ Passed' : '❌ Failed'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ElitureMockExam = ({ user }) => {
  const [view, setView] = useState('options');
  const [category, setCategory] = useState('GenAI');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testHistory, setTestHistory] = useState([]);

  const startExam = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/mockquestions?category=${category}`);
      const list = toArr(res.data);
      setQuestions(list.length ? list : sampleQuestions.filter(q => q.category === category));
    } catch {
      setQuestions(sampleQuestions);
    }
    setLoading(false);
    setView('exam');
  };

  const handleSaveResult = (result) => {
    setTestHistory(prev => [...prev, result]);
  };

  const tracks = [
    { key: 'GenAI', label: 'Generative AI',    icon: '🤖', desc: '40 Questions · 60 min', color: '#6366f1' },
    { key: 'Cloud', label: 'Cloud Architecture', icon: '☁️', desc: '35 Questions · 55 min', color: '#10b981' },
    { key: 'ML',    label: 'Machine Learning',   icon: '🧠', desc: '45 Questions · 65 min', color: '#f59e0b' },
    { key: 'DevOps', label: 'DevOps & CI/CD',   icon: '⚙️', desc: '30 Questions · 50 min', color: '#ec4899' },
  ];

  if (view === 'exam') {
    return (
      <div>
        <button onClick={() => setView('options')} style={{ background: 'none', border: 'none',
          color: C.textMuted, cursor: 'pointer', marginBottom: 24,
          fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to Tests
        </button>
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,102,241,0.15)',
          borderRadius: 20, padding: 32 }}>
          <QuizView
            questions={questions}
            category={category}
            onFinish={() => setView('options')}
            onSaveResult={handleSaveResult}
            userId={user?.id}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>Mock Test Center</div>
        <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
          Practice with real exam-style questions and detailed explanations
        </div>
      </div>

      {/* Track selector */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 16 }}>
          Select Certification Track
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
          {tracks.map(t => (
            <div key={t.key} onClick={() => setCategory(t.key)} style={{
              background: category === t.key ? `${t.color}20` : C.card,
              border: `1px solid ${category === t.key ? t.color : C.cardBorder}`,
              borderRadius: 14, padding: 20, cursor: 'pointer', transition: 'all 0.2s',
              boxShadow: category === t.key ? `0 0 20px ${t.color}30` : 'none',
            }}
              onMouseEnter={e => { if (category !== t.key) e.currentTarget.style.borderColor = t.color; }}
              onMouseLeave={e => { if (category !== t.key) e.currentTarget.style.borderColor = C.cardBorder; }}
            >
              <div style={{ fontSize: 28, marginBottom: 10 }}>{t.icon}</div>
              <div style={{ fontWeight: 700, color: '#fff', marginBottom: 4, fontSize: '0.95rem' }}>{t.label}</div>
              <div style={{ fontSize: '0.75rem', color: C.textMuted }}>{t.desc}</div>
              {category === t.key && (
                <div style={{ marginTop: 10, fontSize: '0.72rem', color: t.color, fontWeight: 700 }}>✓ Selected</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Launch panel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 28 }}>
        <div style={{ background: C.card, border: '1px solid rgba(99,102,241,0.15)', borderRadius: 14, padding: 22 }}>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
            letterSpacing: 1, marginBottom: 12 }}>Before You Begin</div>
          {[
            'Read each question carefully before answering',
            'You can navigate between questions freely',
            'Submit each answer to reveal the explanation',
            'Timer counts down from exam start',
            'Results are saved automatically on completion',
            'Score 60%+ to earn your certificate',
          ].map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10, fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>
              <span style={{ color: C.secondary, flexShrink: 0 }}>✓</span> {tip}
            </div>
          ))}
        </div>

        <div style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
          borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
              letterSpacing: 1, marginBottom: 16 }}>Selected Track</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#fff', marginBottom: 6 }}>
              {tracks.find(t => t.key === category)?.label}
            </div>
            <div style={{ fontSize: '0.85rem', color: C.textMuted }}>
              {tracks.find(t => t.key === category)?.desc}
            </div>
            <div style={{ marginTop: 12, fontSize: '0.8rem', color: C.secondary }}>
              🏆 Pass with 60%+ to earn a certificate
            </div>
          </div>
          <button onClick={startExam} disabled={loading} style={{
            background: loading ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg, #6366f1, #10b981)',
            border: 'none', borderRadius: 12, padding: '14px 28px',
            color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '1rem', marginTop: 20, boxShadow: '0 8px 24px rgba(99,102,241,0.3)',
            width: '100%',
          }}>
            {loading ? 'Loading Questions...' : 'Start Mock Exam →'}
          </button>
        </div>
      </div>

      <TestHistory history={testHistory} />
    </div>
  );
};

export default ElitureMockExam;
